import { DiscountContract, DiscountType, VoidCompReasonModel } from 'models'
import { IServiceCharge } from 'store/models/sellModels'
import Decimal from 'decimal.js'
import {
    distributeDiscountValueToLines,
    splitedOrderDiscountToLine,
} from './discountCalculation'
import { calculateOrderServiceCharge } from './serviceChargeCalculation'
import { round } from './common'
import calculateTax from './taxCalculation'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { isEmpty } from 'lodash-es'
import {
    LineModelExtend,
    ICalculationResultModel,
    LineResultModel,
} from './models/IOrderCalculationModel'
import { isVoidedItem } from '../orderItemUtil'

function calculateExcludeFromItemTaxAmount(
    line: LineModelExtend,
    parentLine?: LineModelExtend,
) {
    line.excludeFromItemTaxAmount = line.excludeFromItemTaxAmount.plus(
        sumDecimal(
            line.modifierLines
                .filter((a) => !isEmpty(a.tax))
                .map((a) => a.subtotal),
        )
            .mul(line.amount)
            .mul(parentLine?.amount ?? 1),
    )

    line.comboItems.forEach((combo) => {
        line.excludeFromItemTaxAmount = isEmpty(combo.tax)
            ? line.excludeFromItemTaxAmount.plus(combo.excludeFromItemTaxAmount)
            : line.excludeFromItemTaxAmount.plus(
                  combo.subtotal.mul(line.amount),
              )
    })
}

function calculateLineTax(
    line: LineModelExtend,
    splitedLine: LineModelExtend[],
    parentLineQuantity: number = 1,
) {
    const splitedOrderDiscount =
        splitedLine.find((a) => a.orderItemId === line.orderItemId)
            ?.orderDiscountAmount ?? new Decimal(0)
    const productValue = line.subtotal
        .mul(parentLineQuantity)
        .minus(line.excludeFromItemTaxAmount)
        .minus(splitedOrderDiscount)

    const itemTaxAmount = calculateTax(
        productValue,
        line.tax?.rate,
        line.isTaxInclusive,
    )

    let totalTax = itemTaxAmount
    let taxExclusive = new Decimal(0)
    let taxInclusive = new Decimal(0)
    if (line.isTaxInclusive) taxInclusive = taxInclusive.plus(itemTaxAmount)
    else taxExclusive = taxExclusive.plus(itemTaxAmount)
    line.modifierLines.forEach((modLine) => {
        const modDiscount =
            splitedLine.find((a) => a.orderItemId === modLine.orderItemId)
                ?.orderDiscountAmount ?? new Decimal(0)
        const modTax = modLine.tax
        if (!modTax) return
        const modValue = modLine.subtotal
            .mul(line.amount)
            .mul(parentLineQuantity)
            .minus(modDiscount.toNumber())

        const modLineTax = calculateTax(
            modValue,
            modLine.tax?.rate,
            modLine.isTaxInclusive,
        )
        totalTax = totalTax.plus(modLineTax)
        if (modLine.isTaxInclusive) taxInclusive = taxInclusive.plus(modLineTax)
        else taxExclusive = taxExclusive.plus(modLineTax)
    })
    return {
        name: line.itemName,
        orderItemId: line.orderItemId,
        taxAmount: totalTax,
        taxExclusive,
        taxInclusive,
        isTaxInclusive: line.isTaxInclusive ?? false,
        sortingIndex: line.sortingIndex,

        orderDiscount: line.orderDiscountAmount,
        orderLineDiscount: line.itemDiscountAmount ?? 0,
        excludeFromItemTaxAmount: line.excludeFromItemTaxAmount,
        subtotal: line.subtotal,
        subLineResults: [],
    }
}

function summarizeOrderLineTax(itemLine: LineModelExtend): LineResultModel {
    itemLine.comboItems.forEach((c) => {
        calculateExcludeFromItemTaxAmount(c, itemLine)
    })
    calculateExcludeFromItemTaxAmount(itemLine)

    const splitedLines: LineModelExtend[] = applyDiscountToProduct(
        itemLine,
        itemLine.comboItems,
    )

    const result = itemLine.comboItems.map((comboItemLine) => {
        return calculateLineTax(comboItemLine, splitedLines, itemLine.amount)
    })
    result.push(calculateLineTax(itemLine, splitedLines))

    return {
        name: itemLine.itemName,
        orderItemId: itemLine.orderItemId,
        taxAmount: sumDecimal(result.map((a) => a.taxAmount)).toNumber(),
        taxExclusive: sumDecimal(result.map((a) => a.taxExclusive)).toNumber(),
        taxInclusive: sumDecimal(result.map((a) => a.taxInclusive)).toNumber(),
        isTaxInclusive: itemLine.isTaxInclusive ?? false,
        sortingIndex: itemLine.sortingIndex,

        orderDiscount: itemLine.orderDiscountAmount?.toNumber() ?? 0,
        orderLineDiscount: itemLine.itemDiscountAmount ?? 0,
        subtotal: itemLine.subtotal?.toNumber() ?? 0,
        excludeFromItemTaxAmount:
            itemLine.excludeFromItemTaxAmount?.toNumber() ?? 0,
        subLineResults: result.map((a) => {
            return {
                name: a.name,
                orderItemId: a.orderItemId,
                taxAmount: a.taxAmount.toNumber(),
                taxExclusive: a.taxExclusive.toNumber(),
                taxInclusive: a.taxInclusive.toNumber(),
                isTaxInclusive: a.isTaxInclusive ?? false,
                sortingIndex: a.sortingIndex,

                orderDiscount: a.orderDiscount?.toNumber() ?? 0,
                orderLineDiscount: a.orderLineDiscount ?? 0,
                subtotal: a.subtotal?.toNumber() ?? 0,
                excludeFromItemTaxAmount:
                    a.excludeFromItemTaxAmount?.toNumber() ?? 0,
                subLineResults: [],
            }
        }),
    }
}
/**
 * calculate order line tax and order totaldue
 * totaltax, service charge, order discount
 * @param orderLines
 * @param serviceCharge
 * @param entireDiscounts
 * @param entireCompDiscount
 * @param isCalculateCompAfterDiscount
 * @returns
 */
function calculateOrder(
    orderLines: LineModelExtend[],
    serviceCharge: IServiceCharge,
    entireDiscounts: DiscountContract[] = [],
    entireCompDiscount: VoidCompReasonModel | undefined = undefined,
    isCalculateCompAfterDiscount: boolean,
): ICalculationResultModel {
    // Deposit-and-Credit Lines
    // No Tax / No Service Charge / No Discount
    const depositAndCreditSubtotal = round(
        orderLines
            .filter((line) => line.isDepositAndCreditItem ?? false)
            .reduce((prev, cur) => {
                return new Decimal(cur.lineTotal ?? 0)
                    .mul(cur.amount!!)
                    .plus(prev)
            }, new Decimal(0)),
    )

    // Positive Priced Lines
    // NOTE: Negative Priced Lines should not be included in order calculation
    let itemLines = orderLines
        .filter((l) => !isVoidedItem(l))
        .filter((line) => !line.isDepositAndCreditItem)

    const orderSubtotal = sumDecimal(
        itemLines.map((a) => a.subtotal ?? new Decimal(0)),
    )

    let totalDiscount
    ;({ totalDiscount, itemLines } = summarizeOrderDiscount(
        itemLines,
        orderSubtotal,
        entireDiscounts,
        entireCompDiscount,
        isCalculateCompAfterDiscount,
    ))

    const taxLines = itemLines.map((line) => summarizeOrderLineTax(line))
    const {
        orderServiceChargeTax,
        orderServiceCharge,
    } = applyOrderServiceCharge(serviceCharge, itemLines, entireCompDiscount)

    const {
        totalSubTotal,
        totalDue,
        totalTax,
        totalExclusiveTax,
        totalInclusiveTax,
    } = finalizeOrder(
        taxLines,
        serviceCharge,
        orderServiceChargeTax,
        orderSubtotal,
        depositAndCreditSubtotal,
        totalDiscount,
        orderServiceCharge,
    )

    const rlt: ICalculationResultModel = {
        taxLines,
        subtotal: totalSubTotal.toNumber(),
        totalDiscount: totalDiscount.toNumber(),
        totalServiceCharge: orderServiceCharge.toNumber(),
        totalDue: totalDue.toNumber(),
        totalTax: totalTax.toNumber(),
        totalExclusiveTax: totalExclusiveTax.toNumber(),
        totalInclusiveTax: totalInclusiveTax.toNumber(),
    }
    return rlt
}
function applyDiscountToProduct(
    itemLine: LineModelExtend,
    combosLines: LineModelExtend[],
) {
    const modLines = itemLine.modifierLines.filter(
        (a) => !!a.tax && a.subtotal.greaterThan(0),
    )

    const comboTaxLines = combosLines.filter(
        (a) =>
            !!a.tax &&
            a.subtotal
                .mul(itemLine.amount)
                .minus(a.excludeFromItemTaxAmount)
                .greaterThan(0),
    )
    const totalLineDiscount = new Decimal(itemLine.totalCompDiscount ?? 0)
        .plus(itemLine.itemDiscountAmount ?? 0)
        .plus(itemLine.orderDiscountAmount)

    const noNeedToSplitedDiscount =
        (isEmpty(modLines) &&
            isEmpty(comboTaxLines) &&
            !combosLines.some((a) =>
                a.modifierLines.some((m) => !!m.tax && m.lineTotal > 0),
            )) ||
        totalLineDiscount.equals(0)
    if (noNeedToSplitedDiscount) {
        return [{ ...itemLine, orderDiscountAmount: totalLineDiscount }]
    }

    const splitedLines: LineModelExtend[] = []
    if (
        itemLine.subtotal
            .minus(itemLine.excludeFromItemTaxAmount)
            .greaterThan(0)
    )
        splitedLines.push({
            ...itemLine,
            itemDiscountAmount: itemLine.excludeFromItemTaxAmount.toNumber(),
            totalCompDiscount: 0,
            //result
            orderDiscountAmount: new Decimal(0),
            subtotal: itemLine.subtotal,
        })
    comboTaxLines.forEach((comboLine) =>
        splitedLines.push({
            ...comboLine,
            itemDiscountAmount: comboLine.excludeFromItemTaxAmount.toNumber(),
            totalCompDiscount: 0,
            //result
            orderDiscountAmount: new Decimal(0),
            subtotal: comboLine.subtotal.mul(itemLine.amount),
        }),
    )
    modLines.forEach((modLine) =>
        splitedLines.push({
            ...modLine,
            itemDiscountAmount: 0,
            totalCompDiscount: 0,
            //result
            orderDiscountAmount: new Decimal(0),
            subtotal: modLine.subtotal.mul(itemLine.amount),
        }),
    )
    combosLines.forEach((comboLine) =>
        comboLine.modifierLines
            .filter((a) => !!a.tax && a.lineTotal > 0)
            .forEach((comboModLine) => {
                splitedLines.push({
                    ...comboModLine,
                    itemDiscountAmount: comboModLine.excludeFromItemTaxAmount.toNumber(),
                    totalCompDiscount: 0,
                    //result
                    orderDiscountAmount: new Decimal(0),
                    subtotal: comboModLine.subtotal
                        .mul(itemLine.amount)
                        .mul(comboLine.amount),
                })
            }),
    )
    const splitedResults = distributeDiscountValueToLines(
        {
            type: DiscountType.FixedAmount,
            value: totalLineDiscount.toNumber(),
        },
        itemLine.subtotal,
        splitedLines,
        false,
    )

    splitedResults.forEach((r, i) => {
        splitedLines[i].orderDiscountAmount = splitedLines[
            i
        ].orderDiscountAmount.plus(r.splitedResult)
    })

    return splitedLines
}
function finalizeOrder(
    taxLines: LineResultModel[],
    serviceCharge: IServiceCharge,
    orderServiceChargeTax: Decimal,
    orderSubtotal: Decimal,
    negativeSubtotal: Decimal,
    totalDiscount: Decimal,
    orderServiceCharge: Decimal,
) {
    let totalInclusiveTax = taxLines.reduce(
        (prev, cur) => new Decimal(cur.taxInclusive!!).plus(prev),
        new Decimal(0),
    )

    let totalExclusiveTax = taxLines.reduce(
        (prev, cur) => new Decimal(cur.taxExclusive!!).plus(prev),
        new Decimal(0),
    )

    if (serviceCharge.isTaxInclusive) {
        totalInclusiveTax = totalInclusiveTax.plus(orderServiceChargeTax)
    } else {
        totalExclusiveTax = totalExclusiveTax.plus(orderServiceChargeTax)
    }

    const totalTax = totalExclusiveTax.plus(totalInclusiveTax)
    const totalSubTotal = orderSubtotal.plus(negativeSubtotal)

    const totalDue = round(
        totalSubTotal
            .sub(totalDiscount)
            .plus(totalExclusiveTax)
            .plus(orderServiceCharge),
    )
    return {
        totalSubTotal,
        totalDue,
        totalTax,
        totalExclusiveTax,
        totalInclusiveTax,
    }
}

function applyOrderServiceCharge(
    serviceCharge: IServiceCharge,
    itemLines: LineModelExtend[],
    entireCompDiscount: VoidCompReasonModel | undefined,
) {
    const orderServiceCharge = round(
        calculateOrderServiceCharge(
            serviceCharge,
            itemLines,
            Boolean(entireCompDiscount),
        ),
    )

    const orderServiceChargeTax = round(
        calculateTax(
            orderServiceCharge,
            serviceCharge.taxes?.find(
                (i) => i.taxID === serviceCharge.setting?.taxID,
            )?.rate,
            serviceCharge.isTaxInclusive,
        ),
    )
    return { orderServiceChargeTax, orderServiceCharge }
}
/**
 * apply order discount to each lines
 * @param itemLines
 * @param orderSubtotal
 * @param entireDiscounts
 * @param entireCompDiscount
 * @param isCalculateCompAfterDiscount
 * @returns
 */
function summarizeOrderDiscount(
    itemLines: LineModelExtend[],
    orderSubtotal: Decimal,
    entireDiscounts: DiscountContract[],
    entireCompDiscount: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
) {
    const totalLineDiscount = sumDecimal(
        itemLines.map((a) =>
            new Decimal(a.itemDiscountAmount ?? 0).plus(
                a.totalCompDiscount ?? 0,
            ),
        ),
    )
    const orderItemNetsubtotal = orderSubtotal.sub(totalLineDiscount)
    itemLines = splitedOrderDiscountToLine(
        itemLines,
        orderItemNetsubtotal,
        entireDiscounts,
        entireCompDiscount,
        isCalculateCompAfterDiscount,
    )
    const totalEntireDiscount = sumDecimal(
        itemLines.map((l) => l.orderDiscountAmount),
    )

    const totalDiscount = Decimal.min(
        totalLineDiscount.plus(totalEntireDiscount),
        orderSubtotal,
    )
    return { totalDiscount, itemLines }
}
export default calculateOrder
